import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';

interface LLMSuggestProps {
  context: string;
  value: string;
  additionalContext?: Record<string, string>;
  variant: 'trigger' | 'typeahead';
  onSelect: (suggestion: string) => void;
  /** Fired when a suggestion run STARTS, with the value it was asked about.
   *  A field that wants to do something with the words the user already has —
   *  the boardroom form names the room from them — needs to know the press
   *  happened, not just which suggestion was picked afterwards. */
  onRun?: (value: string) => void;
  /** Human-readable field name shown at the top of the dropdown so the user
   *  knows which field the suggestions belong to.  Defaults to a formatted
   *  version of the context string (e.g. "niche_description" → "Niche Description"). */
  label?: string;
  count?: number;
  debounceMs?: number;
  minChars?: number;
  /** Trigger variant: the button stays off until the field holds this many words.
   *  A free-text brief with nothing typed yields six generic guesses; three or four
   *  words is enough to say what the room is about, and the suggestions build on it. */
  minWords?: number;
  disabled?: boolean;
  multiSelect?: boolean;
}

/** Whitespace-separated words in a value; "  " and "" are zero. */
export function wordCount(value: string): number {
  return value.trim() ? value.trim().split(/\s+/).length : 0;
}

interface DropdownPos {
  top: number;
  left: number;
  width: number;
  // Inline cap so the dropdown can't extend past the viewport edge — the
  // anchor might sit near the bottom of the page, in which case `position:
  // fixed` would otherwise let the dropdown disappear below the fold.
  maxHeight: number;
}

const PREFERRED_MAX_HEIGHT = 288; // matches the legacy Tailwind `max-h-72`
const MIN_USEFUL_HEIGHT = 160; // below this, prefer flipping above

function computePosition(anchorEl: HTMLElement, minWidth = 320): DropdownPos {
  const refEl = anchorEl.parentElement ?? anchorEl;
  const rect = refEl.getBoundingClientRect();
  const pad = 12;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // On mobile (<640px) go nearly full-width; otherwise use parent width or minWidth
  let width = vw < 640 ? vw - pad * 2 : Math.max(rect.width, minWidth);
  if (width > vw - pad * 2) width = vw - pad * 2;
  // Right-align: position so the dropdown's right edge aligns with the anchor's right edge
  let left = rect.right - width;
  if (left + width > vw - pad) left = vw - pad - width;
  if (left < pad) left = pad;

  // Available space below vs above the anchor; flip up when below is too
  // cramped and there's clearly more room above.
  const gap = 6;
  const availBelow = Math.max(0, vh - rect.bottom - gap - pad);
  const availAbove = Math.max(0, rect.top - gap - pad);
  const flipAbove = availBelow < MIN_USEFUL_HEIGHT && availAbove > availBelow;

  let top: number;
  let maxHeight: number;
  if (flipAbove) {
    maxHeight = Math.min(PREFERRED_MAX_HEIGHT, availAbove);
    top = rect.top - gap - maxHeight;
    if (top < pad) top = pad;
  } else {
    maxHeight = Math.min(
      PREFERRED_MAX_HEIGHT,
      Math.max(MIN_USEFUL_HEIGHT, availBelow),
    );
    top = rect.bottom + gap;
  }
  return { top, left, width, maxHeight };
}

function formatContextLabel(context: string): string {
  return context.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function LLMSuggest({
  context,
  value,
  additionalContext,
  variant,
  onSelect,
  onRun,
  label,
  count = 5,
  debounceMs = 300,
  minChars = 2,
  minWords = 0,
  disabled = false,
  multiSelect = false,
}: LLMSuggestProps) {
  const fieldLabel = label ?? formatContextLabel(context);
  const wordsShort = Math.max(0, minWords - wordCount(value));
  const tooShort = wordsShort > 0;
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [open, setOpen] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const [pos, setPos] = useState<DropdownPos | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track the value that produced current suggestions (for typeahead resets)
  const fetchedForValue = useRef<string>('');
  // Track items selected in multiSelect mode (excluded from future fetches)
  const selectedRef = useRef<string[]>([]);
  const [selectedSet, setSelectedSet] = useState<Set<string>>(new Set());
  // Suppress the next typeahead fetch after a selection replaces the value
  const suppressNextFetch = useRef(false);
  // Track whether the sibling input is focused (typeahead only opens when focused)
  const [inputFocused, setInputFocused] = useState(false);
  // Monotonically increasing counter — increment to cancel any in-flight fetch
  // so a stale response from a previous field cannot re-open this dropdown.
  const fetchGenRef = useRef(0);

  // Keep latest props in refs so callbacks/effects don't depend on them
  const contextRef = useRef(context);
  const additionalContextRef = useRef(additionalContext);
  const countRef = useRef(count);
  contextRef.current = context;
  additionalContextRef.current = additionalContext;
  countRef.current = count;

  const updatePosition = useCallback(() => {
    if (!anchorRef.current) return;
    setPos(computePosition(anchorRef.current));
  }, []);

  // Track focus on the sibling input (parent container's focusin/focusout)
  useEffect(() => {
    if (variant !== 'typeahead') return;
    const parent = anchorRef.current?.parentElement;
    if (!parent) return;
    const onFocusIn = () => setInputFocused(true);
    const onFocusOut = (e: FocusEvent) => {
      // Stay "focused" if focus moved to the dropdown or another child of the parent
      const related = e.relatedTarget as Node | null;
      if (
        (parent.contains(related)) ||
        (dropdownRef.current && dropdownRef.current.contains(related))
      ) return;
      // Cancel any in-flight fetch so a stale response can't re-open this dropdown
      fetchGenRef.current++;
      setInputFocused(false);
      setOpen(false);
    };
    parent.addEventListener('focusin', onFocusIn);
    parent.addEventListener('focusout', onFocusOut);
    return () => {
      parent.removeEventListener('focusin', onFocusIn);
      parent.removeEventListener('focusout', onFocusOut);
    };
  }, [variant]);

  // Close on click outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        anchorRef.current && !anchorRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  /** Initial fetch — replaces suggestions list. Reads context/additionalContext from refs. */
  const fetchSuggestions = useCallback(async (currentValue: string) => {
    // Stamp the generation at call time; if it changes before we finish, discard result.
    const gen = ++fetchGenRef.current;
    setLoading(true);
    setExhausted(false);
    selectedRef.current = [];
    setSelectedSet(new Set());
    try {
      const results = await api.getAutocompleteSuggestions(
        contextRef.current, currentValue, additionalContextRef.current, countRef.current,
      );
      if (gen !== fetchGenRef.current) return; // focus moved away — discard stale response
      setSuggestions(results);
      fetchedForValue.current = currentValue;
      if (results.length > 0) {
        updatePosition();
        setOpen(true);
      } else {
        setOpen(false);
      }
      if (results.length < countRef.current) setExhausted(true);
    } catch {
      if (gen !== fetchGenRef.current) return;
      setSuggestions([]);
      setOpen(false);
    } finally {
      if (gen === fetchGenRef.current) setLoading(false);
    }
  }, [updatePosition]); // stable — no prop deps

  /** Load more — appends to existing suggestions */
  const fetchMore = useCallback(async () => {
    if (loadingMore || exhausted) return;
    setLoadingMore(true);
    try {
      const exclude = [...suggestions, ...selectedRef.current];
      const results = await api.getAutocompleteSuggestions(
        contextRef.current, fetchedForValue.current, additionalContextRef.current, countRef.current,
        exclude,
      );
      if (results.length === 0) {
        setExhausted(true);
      } else {
        const deduped = results.filter(r => !suggestions.includes(r));
        if (deduped.length === 0) {
          setExhausted(true);
        } else {
          setSuggestions(prev => [...prev, ...deduped]);
          if (results.length < countRef.current) setExhausted(true);
        }
      }
    } catch {
      setExhausted(true);
    } finally {
      setLoadingMore(false);
    }
  }, [suggestions, loadingMore, exhausted]);

  /** Scroll handler — fetch more when near the bottom */
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (nearBottom && !loadingMore && !exhausted) {
      fetchMore();
    }
  }, [fetchMore, loadingMore, exhausted]);

  // Typeahead: debounced fetch ONLY when `value` changes AND input is focused
  useEffect(() => {
    if (variant !== 'typeahead') return;
    if (disabled || !inputFocused) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (suppressNextFetch.current) {
      suppressNextFetch.current = false;
      return;
    }
    if (value.length < minChars) {
      // An emptied input does NOT tear down an open multi-select list. The
      // chip inputs clear their draft on every pick, so this branch used to
      // fire immediately after the first selection and throw away a list that
      // cost a full LLM round-trip — you got one chip per generation, and the
      // dropdown flickered off on each one. Picks now accumulate against the
      // same list, which is what `multiSelect` was for.
      if (multiSelect && open) return;
      setOpen(false);
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, debounceMs);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, variant, disabled, inputFocused, minChars, debounceMs]);

  const handleTriggerClick = () => {
    if (disabled || loading || tooShort) return;
    if (open) { setOpen(false); return; }
    onRun?.(value);
    fetchSuggestions(value);
  };

  const handleSelect = (suggestion: string) => {
    onSelect(suggestion);
    if (multiSelect) {
      selectedRef.current = [...selectedRef.current, suggestion];
      setSelectedSet(prev => { const next = new Set(Array.from(prev)); next.add(suggestion); return next; });
    } else {
      suppressNextFetch.current = true;
      setOpen(false);
      setSuggestions([]);
      setExhausted(false);
    }
  };

  // ── Portal dropdown ──────────────────────────────────────────────
  const dropdown = open && suggestions.length > 0 && pos && createPortal(
    <div
      ref={dropdownRef}
      onScroll={handleScroll}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: pos.width,
        maxHeight: pos.maxHeight,
        zIndex: 9999,
      }}
      className="overflow-y-auto bg-white dark:bg-gray-800 border border-purple-200 dark:border-purple-800 rounded-lg shadow-xl ring-1 ring-purple-100"
    >
      {/* Field label — makes it unambiguous which input these suggestions belong to */}
      <div className="px-3 py-1.5 text-[11px] font-semibold text-purple-500 uppercase tracking-wider border-b border-purple-100 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/30 sticky top-0">
        Suggestions for {fieldLabel}
      </div>
      {suggestions.map((s, i) => {
        const picked = selectedSet.has(s);
        return (
          <button
            key={i}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => !picked && handleSelect(s)}
            disabled={picked}
            className={`w-full text-left px-4 py-3 text-sm sm:text-base border-b border-gray-100 dark:border-gray-700 last:border-b-0 break-words transition-colors ${
              picked
                ? 'text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800 cursor-default'
                : 'text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:text-purple-700 dark:hover:text-purple-300 active:bg-purple-100 dark:active:bg-purple-900/40'
            }`}
          >
            <span className="flex items-center gap-2">
              {picked && (
                <svg className="w-4 h-4 shrink-0 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              )}
              <span>{s}</span>
            </span>
          </button>
        );
      })}
      {loadingMore && (
        <div className="flex justify-center py-3">
          <svg className="w-5 h-5 animate-spin text-purple-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      )}
      {!loadingMore && !exhausted && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={fetchMore}
          className="w-full text-center px-4 py-2.5 text-sm text-gray-400 dark:text-gray-500 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 active:bg-purple-100 dark:active:bg-purple-900/40 border-t border-gray-200 dark:border-gray-700 transition-colors"
        >
          More suggestions...
        </button>
      )}
    </div>,
    document.body,
  );

  // ── Trigger variant ──────────────────────────────────────────────
  if (variant === 'trigger') {
    return (
      <div ref={anchorRef} className="inline-flex shrink-0">
        <button
          type="button"
          onClick={handleTriggerClick}
          disabled={disabled || loading || tooShort}
          title={tooShort ? `Type ${wordsShort} more word${wordsShort === 1 ? '' : 's'} first` : 'Get AI suggestions'}
          data-testid="llm-suggest-trigger"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 bg-purple-50 dark:bg-purple-900/30 hover:bg-purple-100 dark:hover:bg-purple-900/40 active:bg-purple-150 border border-purple-200 dark:border-purple-800 rounded-md transition-colors disabled:opacity-50"
        >
          {loading ? (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
            </svg>
          )}
          <span className="hidden sm:inline">Quick Suggest</span>
        </button>
        {dropdown}
      </div>
    );
  }

  // ── Typeahead variant ────────────────────────────────────────────
  return (
    <div
      ref={anchorRef}
      className={`absolute left-0 right-0 top-0 bottom-0 pointer-events-none rounded-md transition-shadow ${
        open || loading ? 'ring-2 ring-purple-400 ring-offset-0' : ''
      }`}
    >
      {/* Trigger button — top-right of the input */}
      <button
        type="button"
        onClick={handleTriggerClick}
        disabled={disabled || loading}
        title="Get AI suggestions"
        className={`pointer-events-auto absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded transition-colors disabled:opacity-50 ${
          open || loading
            ? 'text-purple-500 bg-purple-50 dark:bg-purple-900/30'
            : 'text-gray-300 dark:text-gray-600 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30'
        }`}
      >
        {loading ? (
          <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
          </svg>
        )}
      </button>
      {dropdown}
    </div>
  );
}
